import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabasePublic } from '../lib/supabasePublic';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  User, 
  Phone, 
  Check, 
  X, 
  MessageSquare,
  Trophy,
  Users,
  Loader2,
  AlertCircle,
  CheckCircle,
  Share2
} from 'lucide-react';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Game {
  id: string;
  titulo: string;
  title: string;
  datetime: string;
  location: string | null;
  public_token: string;
  created_by: string;
  grupo_id: string;
}

interface Confirmation {
  id: string;
  player_name: string;
  player_phone: string | null;
  confirmed_at: string;
  status: 'confirmed' | 'declined';
  notes: string | null;
}

interface Player {
  id: string;
  nome: string;
  telefone: string | null;
  tipo: string;
}
const GameConfirmationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [game, setGame] = useState<Game | null>(null);
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [useExistingPlayer, setUseExistingPlayer] = useState(true); // Já inicia com "Jogador do time"
const [selectedPlayerId, setSelectedPlayerId] = useState('');
  
  // Form states
  const [playerName, setPlayerName] = useState('');
  const [playerPhone, setPlayerPhone] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'declined'>('confirmed');
  const [notes, setNotes] = useState('');
  const [playerPosition, setPlayerPosition] = useState('');
  const [playerLevel, setPlayerLevel] = useState(3);
  const [countryCode, setCountryCode] = useState('+55');

  // Função para fazer parsing correto da data/hora
  const parseGameDateTime = (dateTimeString: string) => {
    try {
      // Primeiro tentar como ISO string
      const isoDate = new Date(dateTimeString);
      if (!isNaN(isoDate.getTime())) {
        return isoDate;
      }
      
      // Se falhar, tentar parse manual
      if (dateTimeString.includes(' ')) {
        return parse(dateTimeString, 'yyyy-MM-dd HH:mm:ss', new Date());
      }
      
      // Fallback para apenas data
      return parse(dateTimeString, 'yyyy-MM-dd', new Date());
    } catch (error) {
      console.error('Erro ao fazer parse da data:', error);
      return new Date(dateTimeString); // Fallback
    }
  };

  useEffect(() => {
  const fetchGameData = async () => {
    if (!token) {
      setError('Token de jogo não fornecido');
      setLoading(false);
      return;
    }

    try {
      // Fetch game data
      const { data: gameData, error: gameError } = await supabasePublic
        .from('jogos')
        .select('*')
        .eq('public_token', token)
        .limit(1)
        .maybeSingle();

      if (gameError) {
        if (gameError.code === 'PGRST116') {
          setError('Jogo não encontrado ou token inválido');
        } else {
          throw gameError;
        }
        setLoading(false);
        return;
      }

      if (!gameData) {
        setError('Jogo não encontrado ou token inválido');
        setLoading(false);
        return;
      }

      // Map 'titulo' from DB to 'title' for display
      setGame({ ...gameData, title: gameData.titulo });

      // Fetch existing confirmations
      const { data: confirmationsData, error: confirmationsError } = await supabasePublic
        .from('game_confirmations')
        .select('*')
        .eq('game_id', gameData.id)
        .order('confirmed_at', { ascending: false });

      if (confirmationsError) {
        console.warn('Error fetching confirmations:', confirmationsError);
      } else {
        setConfirmations(confirmationsData || []);
      }

      // Fetch active players from the group
      const { data: playersData, error: playersError } = await supabasePublic
        .from('jogadores')
        .select('id, nome, telefone, tipo')
        .eq('grupo_id', gameData.grupo_id)
        .eq('ativo', true)
        .order('nome');

      if (playersError) {
        console.warn('Error fetching players:', playersError);
      } else {
        setPlayers(playersData || []);
        if (playersData && playersData.length > 0) {
  setPlayers(playersData);
}

      }
    } catch (error: any) {
      console.error('Error fetching game data:', error);
      setError('Erro ao carregar dados do jogo');
    } finally {
      setLoading(false);
    }
  };

  fetchGameData();
}, [token]);


  const formatPhoneNumber = (value: string, countryCode: string): string => {
    const numbers = value.replace(/\D/g, '');
    
    if (countryCode === '+55') {
      // Brazilian format: (11) 99999-9999
      if (numbers.length <= 11) {
        return numbers
          .replace(/(\d{2})(\d)/, '($1) $2')
          .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
      }
    } else if (countryCode === '+1') {
      // US format: (555) 123-4567
      if (numbers.length <= 10) {
        return numbers
          .replace(/(\d{3})(\d)/, '($1) $2')
          .replace(/(\d{3})(\d{4})$/, '$1-$2');
      }
    } else {
      // European format: simple grouping
      if (numbers.length <= 12) {
        return numbers
          .replace(/(\d{2,3})(\d{3})(\d{3,4})$/, '$1 $2 $3');
      }
    }
    
    return numbers;
  };

  const getPhonePlaceholder = (countryCode: string) => {
    switch (countryCode) {
      case '+55':
        return '(11) 99999-9999';
      case '+1':
        return '(555) 123-4567';
      case '+34':
      case '+33':
      case '+49':
      case '+39':
      case '+44':
      case '+31':
      case '+32':
      case '+41':
      case '+43':
      case '+351':
        return '12 345 6789';
      default:
        return '123456789';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!game) {
      setError('Dados do jogo não encontrados');
      return;
    }

    let finalPlayerName = '';
    let finalPlayerPhone = '';

    if (useExistingPlayer) {
      if (!selectedPlayerId) {
        setError('Selecione um jogador da lista');
        return;
      }
      const selectedPlayer = players.find(p => p.id === selectedPlayerId);
      if (!selectedPlayer) {
        setError('Jogador selecionado não encontrado');
        return;
      }
      finalPlayerName = selectedPlayer.nome;
      finalPlayerPhone = selectedPlayer.telefone || '';
    } else {
      if (!playerName.trim()) {
        setError('Nome do jogador é obrigatório');
        return;
      }
      if (!playerPosition) {
        setError('Posição é obrigatória');
        return;
      }
      finalPlayerName = playerName.trim();
      // Format phone for storage (international format)
      const cleanPhone = playerPhone.replace(/\D/g, '');
      finalPlayerPhone = cleanPhone ? `${countryCode} ${cleanPhone}` : '';
    }

    // Check if player already confirmed
    const existingConfirmation = confirmations.find(
      c => c.player_name.toLowerCase() === finalPlayerName.toLowerCase()
    );

    if (existingConfirmation) {
      setError('Este jogador já confirmou presença para este jogo');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Se é um novo jogador, criar no sistema primeiro
      if (!useExistingPlayer) {
        console.log('Creating new player in system:', {
          nome: finalPlayerName,
          telefone: finalPlayerPhone,
          posicao: playerPosition,
          nivel: playerLevel
        });

        // Verificar se já existe um jogador com este nome no grupo
        const { data: existingPlayer, error: checkError } = await supabasePublic
          .from('jogadores')
          .select('id, nome')
          .eq('grupo_id', game.grupo_id)
          .ilike('nome', finalPlayerName)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error('Error checking existing player:', checkError);
          throw new Error('Erro ao verificar jogador existente');
        }

        if (!existingPlayer) {
          // Criar novo jogador no sistema
          const { data: newPlayer, error: playerError } = await supabasePublic
            .from('jogadores')
            .insert({
              nome: finalPlayerName,
              telefone: finalPlayerPhone || null,
              posicao: playerPosition,
              nivel: playerLevel,
              tipo: 'avulso', // Novos jogadores via confirmação são marcados como avulsos
              idade: 25, // Idade padrão para jogadores criados via confirmação
              grupo_id: game.grupo_id,
              ativo: true
            })
            .select()
            .single();

          if (playerError) {
            console.error('Error creating new player:', playerError);
            throw new Error('Erro ao criar novo jogador no sistema');
          }

          console.log('New player created successfully:', newPlayer);

          // Atualizar lista local de jogadores
          setPlayers(prev => [...prev, {
            id: newPlayer.id,
            nome: newPlayer.nome,
            telefone: newPlayer.telefone,
            tipo: newPlayer.tipo
          }]);
        } else {
          console.log('Player already exists in system:', existingPlayer);
        }
      }

      const { data: confirmation, error: confirmationError } = await supabasePublic
        .from('game_confirmations')
        .insert({
          game_id: game.id,
          player_name: finalPlayerName,
          player_phone: finalPlayerPhone || null,
          status,
          notes: notes.trim() || null
        })
        .select()
        .single();

      if (confirmationError) throw confirmationError;

      // Add to local state
      setConfirmations(prev => [confirmation, ...prev]);
      
      // Reset form
      setUseExistingPlayer(true); // Volta para "Jogador do time" como padrão
      setSelectedPlayerId('');
      setPlayerName('');
      setPlayerPhone('');
      setPlayerPosition('');
      setPlayerLevel(3);
      setCountryCode('+55');
      setStatus('confirmed');
      setNotes('');
      setSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error submitting confirmation:', error);
      setError(error.message || 'Erro ao confirmar presença. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Carregando jogo...</p>
        </div>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark p-4">
        <div className="card p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Jogo não encontrado</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark p-4">
        <div className="card p-8 max-w-md text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Jogo não encontrado</h1>
          <p className="text-gray-500 dark:text-gray-400">
            O jogo que você está procurando não existe ou foi removido.
          </p>
        </div>
      </div>
    );
  }

  const confirmedCount = confirmations.filter(c => c.status === 'confirmed').length;
  const declinedCount = confirmations.filter(c => c.status === 'declined').length;

  const shareConfirmationsList = () => {
    if (!game) return;

    const confirmedPlayers = confirmations.filter(c => c.status === 'confirmed');
    const declinedPlayers = confirmations.filter(c => c.status === 'declined');
    
    const gameDate = game.data ? format(parseGameDateTime(game.data), 'PPP', { locale: ptBR }) : 'Data inválida';
    const gameTime = game.data ? format(parseGameDateTime(game.data), 'HH:mm') : 'Horário inválido';
    
    let message = `*${game.title}*\n\n`;
    message += `*Data:* ${gameDate}\n`;
    message += `*Horário:* ${gameTime}\n`;
    
    if (game.location) {
      message += `*Local:* ${game.location}\n`;
    }
    
    message += `\n *Confirmações:*\n`;
    
    if (confirmedPlayers.length > 0) {
      message += `\n *CONFIRMADOS (${confirmedPlayers.length}):*\n`;
      confirmedPlayers.forEach((player, index) => {
        message += `${index + 1}. ${player.player_name}\n`;
      });
    }
    
    if (declinedPlayers.length > 0) {
      message += `\n *NÃO VÃO (${declinedPlayers.length}):*\n`;
      declinedPlayers.forEach((player, index) => {
        message += `${index + 1}. ${player.player_name}\n`;
      });
    }
    
    if (confirmedPlayers.length === 0 && declinedPlayers.length === 0) {
      message += `Ainda não há confirmações.\n`;
    }
    
    message += `\n *Confirme sua presença:*\n${window.location.href}`;
    message += `\n\n_Enviado via Presida.Club_`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark">
      {/* Header */}
      <div className="bg-white dark:bg-dark-paper shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Trophy className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{game.title}</h1>
              <p className="text-gray-500 dark:text-gray-400">
                Confirme sua presença para o jogo
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Game Info & Confirmation Form */}
          <div className="space-y-6">
            {/* Game Details */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4">Detalhes do Jogo</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Data e Hora</p>
                    <p className="font-medium">
                      {game.data ? format(parseGameDateTime(game.data), 'PPP', { locale: ptBR }) : 'Data inválida'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {game.data ? format(parseGameDateTime(game.data), 'HH:mm') : 'Horário inválido'}
                    </p>
                  </div>
                </div>

                {game.location && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                      <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Local</p>
                      <p className="font-medium">{game.location}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                    <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Confirmações</p>
                    <p className="font-medium">
                      {confirmedCount} confirmados • {declinedCount} recusaram
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirmation Form */}
            <div className="card p-6">
              <h2 className="text-lg font-bold mb-4">Confirmar Presença</h2>

              {success && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p>Presença confirmada com sucesso!</p>
                </div>
              )}

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Como deseja confirmar?
                  </label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-dark-light ${
                      !useExistingPlayer 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-300 dark:border-dark-border'
                    }`}>
                      <input
                        type="radio"
                        name="playerType"
                        checked={!useExistingPlayer}
                        onChange={() => {
                          setUseExistingPlayer(false);
                          setSelectedPlayerId('');
                          setPlayerName('');
                          setPlayerPhone('');
                          setPlayerPosition('');
                          setPlayerLevel(3);
                          setPlayerPosition('');
                          setPlayerLevel(3);
                          setCountryCode('+55');
                        }}
                        className="text-primary-500 focus:ring-primary-500"
                      />
                      <span>Novo jogador</span>
                    </label>
                    
                    {players.length > 0 && (
                      <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-dark-light ${
                        useExistingPlayer 
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                          : 'border-gray-300 dark:border-dark-border'
                      }`}>
                        <input
                          type="radio"
                          name="playerType"
                          checked={useExistingPlayer}
                          onChange={() => {
                            setUseExistingPlayer(true);
                            setPlayerName('');
                            setPlayerPhone('');
                            setCountryCode('+55');
                          }}
                          className="text-primary-500 focus:ring-primary-500"
                        />
                        <span>Jogador do time</span>
                      </label>
                    )}
                  </div>
                </div>

                {useExistingPlayer ? (
                  <div>
                    <label htmlFor="existingPlayer" className="block text-sm font-medium mb-1">
                      Selecione o jogador *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        id="existingPlayer"
                        className="input pl-10"
                        value={selectedPlayerId}
                        onChange={(e) => setSelectedPlayerId(e.target.value)}
                        required
                      >
                        <option value="">Selecione um jogador</option>
                        {players.map((player) => (
                          <option key={player.id} value={player.id}>
                            {player.nome} ({player.tipo})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                <div>
                  <label htmlFor="playerName" className="block text-sm font-medium mb-1">
                    Seu Nome *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="playerName"
                      className="input pl-10"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Digite seu nome"
                      required
                    />
                  </div>
                </div>

                      <option value="lateral">Lateral</option>
                <div>
                  <label htmlFor="playerPhone" className="block text-sm font-medium mb-1">
                    WhatsApp (opcional)
                  </label>
                  <div className="flex gap-2">
                    <select
                      className="input w-32"
                      value={countryCode}
                      onChange={(e) => {
                        setCountryCode(e.target.value);
                        // Reformat phone number when country code changes
                        if (playerPhone) {
                          setPlayerPhone(formatPhoneNumber(playerPhone.replace(/\D/g, ''), e.target.value));
                        }
                      }}
                    >
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+54">🇦🇷 +54</option>
                      <option value="+56">🇨🇱 +56</option>
                      <option value="+57">🇨🇴 +57</option>
                      <option value="+51">🇵🇪 +51</option>
                      <option value="+58">🇻🇪 +58</option>
                      <option value="+598">🇺🇾 +598</option>
                      <option value="+595">🇵🇾 +595</option>
                      <option value="+591">🇧🇴 +591</option>
                      <option value="+593">🇪🇨 +593</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+31">🇳🇱 +31</option>
                      <option value="+32">🇧🇪 +32</option>
                      <option value="+41">🇨🇭 +41</option>
                      <option value="+43">🇦🇹 +43</option>
                      <option value="+351">🇵🇹 +351</option>
                    </select>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        id="playerPhone"
                        className="input pl-10"
                        value={playerPhone}
                        onChange={(e) => setPlayerPhone(formatPhoneNumber(e.target.value, countryCode))}
                        placeholder={getPhonePlaceholder(countryCode)}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="playerPosition" className="block text-sm font-medium mb-1">
                    Posição *
                  </label>
                  <select
                    id="playerPosition"
                    className="input"
                    value={playerPosition}
                    onChange={(e) => setPlayerPosition(e.target.value)}
                    required
                  >
                    <option value="">Selecione uma posição</option>
                    <option value="goleiro">Goleiro</option>
                    <option value="zagueiro">Zagueiro</option>
                    <option value="lateral">Lateral</option>
                    <option value="meio-campo">Meio-campo</option>
                    <option value="atacante">Atacante</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="playerLevel" className="block text-sm font-medium mb-1">
                    Nível *
                  </label>
                  <select
                    id="playerLevel"
                    className="input"
                    value={playerLevel}
                    onChange={(e) => setPlayerLevel(parseInt(e.target.value))}
                    required
                  >
                    <option value="1">1 - Iniciante</option>
                    <option value="2">2 - Básico</option>
                    <option value="3">3 - Intermediário</option>
                    <option value="4">4 - Avançado</option>
                    <option value="5">5 - Profissional</option>
                  </select>
                </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Sua resposta *
                  </label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-dark-light ${
                      status === 'confirmed' 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-300 dark:border-dark-border'
                    }`}>
                      <input
                        type="radio"
                        name="status"
                        value="confirmed"
                        checked={status === 'confirmed'}
                        onChange={(e) => setStatus(e.target.value as 'confirmed' | 'declined')}
                        className="text-primary-500 focus:ring-primary-500"
                      />
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Vou jogar</span>
                    </label>
                    
                    <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-dark-light ${
                      status === 'declined' 
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                        : 'border-gray-300 dark:border-dark-border'
                    }`}>
                      <input
                        type="radio"
                        name="status"
                        value="declined"
                        checked={status === 'declined'}
                        onChange={(e) => setStatus(e.target.value as 'confirmed' | 'declined')}
                        className="text-primary-500 focus:ring-primary-500"
                      />
                      <X className="w-5 h-5 text-red-500" />
                      <span>Não vou jogar</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium mb-1">
                    Observações (opcional)
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      id="notes"
                      className="input pl-10 min-h-[80px]"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Alguma observação sobre sua participação..."
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={submitting || (!useExistingPlayer && (!playerName.trim() || !playerPosition)) || (useExistingPlayer && !selectedPlayerId)}
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Confirmando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      {status === 'confirmed' ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      <span>
                        {status === 'confirmed' ? 'Confirmar Presença' : 'Informar Ausência'}
                      </span>
                    </div>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Confirmations List */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">Lista de Confirmações</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={shareConfirmationsList}
                  className="btn-primary text-sm flex items-center gap-2"
                  title="Compartilhar lista via WhatsApp"
                >
                  <Share2 className="w-4 h-4" />
                  Compartilhar
                </button>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm mb-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>{confirmedCount} confirmados</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span>{declinedCount} recusaram</span>
                </div>
            </div>

            {confirmations.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  Nenhuma confirmação ainda
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Seja o primeiro a confirmar!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {confirmations.map((confirmation) => (
                  <div
                    key={confirmation.id}
                    className={`p-4 rounded-lg border ${
                      confirmation.status === 'confirmed'
                        ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                        : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                          confirmation.status === 'confirmed' ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {confirmation.player_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{confirmation.player_name}</p>
                          {/* 
{confirmation.player_phone && (
  <p className="text-sm text-gray-500">
    {confirmation.player_phone}
  </p>
)} 
*/}

                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {confirmation.status === 'confirmed' ? (
                          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <Check className="w-4 h-4" />
                            <span className="text-sm font-medium">Confirmado</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <X className="w-4 h-4" />
                            <span className="text-sm font-medium">Não vai</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {confirmation.notes && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {confirmation.notes}
                        </p>
                      </div>
                    )}
                    
                    <div className="mt-2 text-xs text-gray-500">
                      {format(new Date(confirmation.confirmed_at), 'PPp', { locale: ptBR })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-dark-paper border-t dark:border-dark-border mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <div className="flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 mb-2">
            <Trophy className="w-5 h-5" />
            <span className="font-medium">Presida.Club</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gestão financeira para times de futebol amador
          </p>
        </div>
      </div>
    </div>
  );
};

export default GameConfirmationPage;